import { useCallback, useEffect, useState } from 'react';
import { ConsentChoice, LEGAL_DOCUMENTS, type ConsentSettings } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { api } from '../lib/api';

const KEY = 'll_consent';
// the choice lives in this browser; storage can be blocked (private windows), so every access is guarded
function readChoice(): ConsentChoice | null {
  try {
    const parsed = ConsentChoice.safeParse(JSON.parse(localStorage.getItem(KEY) ?? 'null'));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
function saveChoice(choice: ConsentChoice) {
  try {
    localStorage.setItem(KEY, JSON.stringify(choice));
  } catch {
    // storage is blocked: the choice holds for this visit only
  }
}

// The cookie choice (UK PECR). Analytics code may only load when `analytics` is true; essential cookies need no
// consent. The banner shows when the super admin has it on and this browser has not answered this version.
export function useConsent() {
  const [settings, setSettings] = useState<ConsentSettings | null>(null);
  const [choice, setChoice] = useState<ConsentChoice | null>(readChoice);
  const [reopened, setReopened] = useState(false);
  useEffect(() => {
    void api
      .consentSettings()
      .then((reply) => setSettings(reply.consent))
      .catch(() => {});
  }, []);
  const answer = useCallback(
    (analytics: boolean) => {
      const next = { version: settings?.version ?? 1, analytics, at: new Date().toISOString() };
      saveChoice(next);
      setChoice(next);
      setReopened(false);
    },
    [settings],
  );
  const current = choice && settings && choice.version === settings.version ? choice : null;
  return {
    settings,
    showBanner: Boolean(settings?.enabled) && (reopened || !current),
    analytics: Boolean(settings?.analyticsEnabled && current?.analytics),
    answer,
    reopen: () => setReopened(true),
  };
}

export function CookieBanner({
  settings,
  onAnswer,
}: {
  settings: ConsentSettings;
  onAnswer: (analytics: boolean) => void;
}) {
  return (
    <aside className="cookie-banner" role="dialog" aria-label="Cookies" aria-live="polite">
      <p>
        <span aria-hidden="true">🍪</span> {settings.message} <a href={LEGAL_DOCUMENTS.privacy.path}>Privacy</a>
      </p>
      <div className="cookie-banner-actions">
        <Button type="button" variant="ghost" onClick={() => onAnswer(false)}>
          Essential only
        </Button>
        {settings.analyticsEnabled && (
          <Button type="button" onClick={() => onAnswer(true)}>
            Accept
          </Button>
        )}
      </div>
    </aside>
  );
}
